import { Box, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { find, map } from 'lodash';
import { useSnackbar } from 'notistack';
import React from 'react';

import { bulkUpdateProcedures } from 'api/procedures';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import { useCasesAndSlidesUpdateMutationsParams } from 'components/StudyDashboard/ProceduresPage/DataGrids/useCasesAndSlidesUpdateMutationParams';
import { ProcedureResponse } from 'interfaces/procedure';
import queryClient from 'utils/queryClient';
import { useCasesParams } from 'utils/useCasesParams';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { ExperimentResultsSelection } from 'utils/useEncodedFilters/types';

export const BulkCaseApprovalModal: React.FC<{
  onCloseModal: () => void;
  internallyApproved: boolean;
}> = ({ internallyApproved, onCloseModal }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { casesParams } = useCasesParams();
  const { encodedFilters } = useEncodedFilters({
    experimentResultsSelection: ExperimentResultsSelection.OnlyQAFailed,
  });
  const { onMutateForCaseBulkUpdate, onError } = useCasesAndSlidesUpdateMutationsParams({
    action: internallyApproved ? 'bulk approve' : 'bulk disapprove',
  });
  const procedureUpdateBulkMutation = useMutation({
    mutationFn: bulkUpdateProcedures,
    onMutate: onMutateForCaseBulkUpdate,
    onError,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['procedures'], exact: false });

      queryClient.setQueriesData<ProcedureResponse>(['procedures', encodedFilters], (oldData) => {
        const newProcedures = map(oldData?.procedures, (p) => find(data, { id: p.id }) || p);
        // if we have this query in the cache, we need to update it to the new result
        return oldData ? { ...oldData, procedures: newProcedures } : oldData;
      });
      enqueueSnackbar({
        message: (
          <Box>
            <Typography variant="body1">
              {internallyApproved ? 'Cases approved successfully' : 'Cases disapproved successfully'}
            </Typography>
          </Box>
        ),
        variant: 'success',
      });
      onCloseModal();
    },
  });
  return (
    <ConfirmationModal
      onConfirm={() =>
        procedureUpdateBulkMutation.mutate({
          procedureUpdates: { internallyApproved },
          ...casesParams,
        })
      }
      onClose={onCloseModal}
      onCancel={onCloseModal}
      title={internallyApproved ? 'Approve Cases' : 'Disapprove Cases'}
      text={
        internallyApproved
          ? 'Are you sure you want to approve the selected cases?'
          : 'Are you sure you want to disapprove the selected cases?'
      }
      confirmButtonProps={{ title: internallyApproved ? 'Approve' : 'Disapprove' }}
    />
  );
};
